import type { TripCashFigure, TripCashSettlementInput } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input } from 'antd';
import { formatMoney } from '../../../shared/policies/format-money';
import { parseMoneyAmount } from '../../../shared/policies/parse-money-amount';

export type SettleMode = 'return' | 'carry';

interface SettleFormValues {
  amount?: string;
  note?: string;
}

interface TripCashSettleDrawerProps {
  figure: TripCashFigure;
  mode: SettleMode | null;
  isSubmitting: boolean;
  error: string | null;
  onSubmit: (input: Omit<TripCashSettlementInput, 'idempotencyKey'>) => Promise<boolean>;
  onClose: () => void;
}

/** Records how one driver's cash for the trip was left: the cash that came back, or the balance carried on the driver's ledger. */
export function TripCashSettleDrawer({ figure, mode, isSubmitting, error, onSubmit, onClose }: TripCashSettleDrawerProps) {
  const [form] = Form.useForm<SettleFormValues>();
  const balance = { amountMinor: Math.abs(figure.balanceMinor), currency: figure.currency };

  async function submit(values: SettleFormValues) {
    const note = values.note?.trim() || undefined;
    const typed = mode === 'return' ? parseMoneyAmount(values.amount, figure.currency) : { ok: true as const, amountMinor: 0 };
    if (!typed.ok) return;
    const done = await onSubmit({ driverId: figure.driverId, currency: figure.currency, returnedMinor: typed.amountMinor, note });
    if (done) form.resetFields();
  }

  function validateReturned(_: unknown, value?: string): Promise<void> {
    const check = parseMoneyAmount(value, figure.currency);
    if (!check.ok) return Promise.reject(new Error(check.reason));
    return check.amountMinor > figure.balanceMinor ? Promise.reject(new Error(`More than the ${formatMoney(balance)} owed`)) : Promise.resolve();
  }

  return (
    <Drawer
      open={mode !== null}
      onClose={onClose}
      title={mode === 'return' ? 'Record cash returned' : 'Carry the balance'}
      placement="right"
      size={480}
      destroyOnHidden
    >
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <p className="mb-4 text-sm text-muted">
        {figure.driverName} {figure.balanceMinor > 0 ? 'owes' : 'is owed'} {formatMoney(balance)} for this trip.
        {mode === 'carry' && ' The balance stays on their ledger; no cash changes hands.'}
      </p>
      <Form<SettleFormValues> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={isSubmitting}>
        {mode === 'return' && (
          <Form.Item name="amount" label="Cash returned" rules={[{ validator: validateReturned }]}>
            <Input inputMode="decimal" prefix={figure.currency} />
          </Form.Item>
        )}
        <Form.Item name="note" label="Note (optional)" rules={[{ max: 200 }]}>
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={isSubmitting}>
            {mode === 'return' ? 'Record cash returned' : 'Carry balance'}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
